// One text field in the planner grid.
//
// Three things every field now does that a bare <textarea> did not:
//
//   * it can be dragged bigger, and remembers the size it was left at;
//   * it starts its text at the top left, whatever the row's height;
//   * it opens into the full formatting editor, and shows the result.
//
// A field holding formatted text shows the formatting rather than its markup.
// Typing into rendered HTML in a table cell is a different (and much larger)
// component than this, so the preview is read-only and offers two ways back to
// editing: the modal, or "Plain text", which drops the formatting explicitly
// instead of losing it the moment someone clicks in the box.

import React, { useCallback, useEffect, useRef } from 'react';
import { Maximize2, Type } from 'lucide-react';

import SafeHtml from '../../../components/SafeHtml';
import './plannerRichText.css';

/**
 * How big each field was dragged, for this page load.
 *
 * In memory, like the plan itself — the planner writes nothing to the browser
 * (see utils/plannerSnapshot.js), and a box height is not worth being the one
 * exception. Sizes are also kept out of the lesson data: how tall a box was
 * left on one screen is not part of the lesson, and putting it in the plan
 * would change every saved JSON file.
 */
const rememberedSizes = new Map();
export const useRememberedSize = (fieldKey) => {
  const elementRef = useRef(null);
  const frameRef = useRef(0);

  const attach = useCallback((element) => {
    elementRef.current = element;
    if (!element || !fieldKey) return;
    const stored = rememberedSizes.get(fieldKey);
    if (stored && Number(stored.height) > 0) element.style.height = `${stored.height}px`;
    if (stored && Number(stored.width) > 0) element.style.width = `${stored.width}px`;
  }, [fieldKey]);

  useEffect(() => {
    const element = elementRef.current;
    if (!element || !fieldKey || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      // Coalesce the burst of callbacks a drag produces into one write.
      window.cancelAnimationFrame(frameRef.current);
      frameRef.current = window.requestAnimationFrame(() => {
        // Only an explicit resize sets these; a width that merely follows the
        // column would otherwise be frozen in place on the next reload.
        const height = Number.parseInt(element.style.height, 10);
        const width = Number.parseInt(element.style.width, 10);
        if (!height && !width) return;
        rememberedSizes.set(fieldKey, { height: height || undefined, width: width || undefined });
      });
    });

    observer.observe(element);
    return () => {
      window.cancelAnimationFrame(frameRef.current);
      observer.disconnect();
    };
  }, [fieldKey]);

  return attach;
};

export const clearRememberedSizes = () => rememberedSizes.clear();

const PlannerTextField = ({
  label,
  // What to call this field in button titles. Rows whose header already names
  // the field pass `label={null}` but keep a name, so the buttons still read
  // "Open Bridge-In in the full editor" rather than "this field".
  name,
  fieldKey,
  value,
  richHtml,
  placeholder,
  minHeight = 96,
  onChange,
  onOpenEditor,
  onClearFormatting
}) => {
  const attachSize = useRememberedSize(fieldKey);
  const fieldName = name || label || 'this field';
  const hasRichText = Boolean(richHtml);

  return (
    // Deliberately not `h-full`: the grid rows are flex containers, so a
    // full-height field would eat the whole cell and push anything after it
    // (an activity's reference material) down over the next row.
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        {label ? <span className="text-sm font-bold text-gray-800">{label}</span> : <span aria-hidden="true" />}
        <div className="flex items-center gap-1">
          {hasRichText && (
            <>
              <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
                Formatted
              </span>
              <button
                type="button"
                onClick={onClearFormatting}
                title={`Edit ${fieldName} as plain text (removes formatting)`}
                aria-label={`Edit ${fieldName} as plain text`}
                className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
              >
                <Type size={14} />
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onOpenEditor}
            title={`Open ${fieldName} in the full editor`}
            aria-label={`Open ${fieldName} in the full editor`}
            className="flex items-center gap-1 rounded border border-gray-300 px-1.5 py-1 text-[11px] font-medium text-gray-600 transition-colors hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700"
          >
            <Maximize2 size={12} />
            Editor
          </button>
        </div>
      </div>

      {hasRichText ? (
        <div
          ref={attachSize}
          onDoubleClick={onOpenEditor}
          title="Double-click to edit"
          className="planner-rich-content planner-resizable w-full cursor-text rounded border border-gray-300 bg-white p-2 text-left align-top text-sm text-gray-800"
          style={{ minHeight: `${minHeight}px` }}
        >
          <SafeHtml html={richHtml} />
        </div>
      ) : (
        <textarea
          ref={attachSize}
          value={value || ''}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          className="planner-resizable w-full rounded border border-gray-300 p-2 text-left align-top focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
          style={{ minHeight: `${minHeight}px` }}
        />
      )}
    </div>
  );
};

export default PlannerTextField;
